import { useCallback, useEffect, useRef, useState } from 'react'
import {
  createWhatsappInstance,
  resolveWhatsappInstanceName,
  getWhatsappInstanceState,
  listWhatsappInstances,
} from '../../db/api.js'
import { useApp } from '../../context/AppContext.jsx'

const POLL_MS = 40_000

const STATUS_LABEL = {
  connecting: 'Conectando — escaneie o QR Code',
  open: 'Conectado',
  close: 'Desconectado',
}

/**
 * Tela de conexão do WhatsApp.
 * - Cria/seleciona a instância do cliente.
 * - Exibe o QR Code e faz polling do estado a cada 40s.
 * - Quando o estado vira "open", para o polling e mostra conectado.
 */
export default function Connect() {
  const { selectedClient } = useApp()
  const selectedClientId = selectedClient?.id || null
  const [instance, setInstance] = useState(null)
  const [qr, setQr] = useState(null)
  const [state, setState] = useState('close')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const timerRef = useRef(null)

  // Carrega a instância existente (se houver) ao montar.
  useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        const instances = await listWhatsappInstances()
        if (!alive) return
        if (instances.length) {
          setInstance(instances[0])
          setState(instances[0].status)
          setQr(instances[0].qr_code)
        }
      } catch (e) {
        if (alive) setError(e.message)
      } finally {
        if (alive) setLoading(false)
      }
    })()
    return () => {
      alive = false
    }
  }, [])

  const poll = useCallback(async (name) => {
    try {
      const r = await getWhatsappInstanceState(name)
      setState(r.state)
      setQr(r.qrcode)
      if (r.state === 'open' && timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
    } catch (e) {
      setError(e.message)
    }
  }, [])

  // Inicia o polling quando há instância e ainda não está conectada.
  useEffect(() => {
    if (!instance || state === 'open') return
    poll(instance.instance_name)
    timerRef.current = setInterval(() => poll(instance.instance_name), POLL_MS)
    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
      timerRef.current = null
    }
  }, [instance, state, poll])

  async function handleCreate() {
    if (!selectedClientId) {
      setError('Selecione um cliente antes de conectar o WhatsApp.')
      return
    }
    setError(null)
    setLoading(true)
    try {
      const instanceName = await resolveWhatsappInstanceName(selectedClientId)
      const created = await createWhatsappInstance({
        instanceName,
        clientId: selectedClientId,
      })
      setInstance(created)
      setState(created.status)
      setQr(created.qr_code)
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  if (loading) return <p>Carregando…</p>

  return (
    <div style={{ maxWidth: 420, margin: '0 auto', textAlign: 'center' }}>
      <h2>Conectar WhatsApp</h2>

      {error && <p style={{ color: 'crimson' }}>{error}</p>}

      {!instance && (
        <button onClick={handleCreate} disabled={loading}>
          Criar instância e gerar QR Code
        </button>
      )}

      {instance && (
        <>
          <p>Status: <strong>{STATUS_LABEL[state] || state}</strong></p>

          {state === 'open' ? (
            <p>✅ WhatsApp conectado{instance.phone ? ` (${instance.phone})` : ''}.</p>
          ) : qr ? (
            <img
              src={qr.startsWith('data:') ? qr : `data:image/png;base64,${qr}`}
              alt="QR Code do WhatsApp"
              style={{ width: 280, height: 280 }}
            />
          ) : (
            <p>Gerando QR Code…</p>
          )}

          {state !== 'open' && (
            <p style={{ fontSize: 12, color: '#666' }}>
              O QR Code atualiza automaticamente a cada 40 segundos.
            </p>
          )}
        </>
      )}
    </div>
  )
}
